import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

function generateSlots(duracionMin) {
  const slots = [];
  const endMin = 19 * 60 + 30; // último slot termina a las 19:30
  for (let m = 9 * 60; m + duracionMin <= endMin; m += duracionMin) {
    slots.push(`${String(Math.floor(m / 60)).padStart(2,'0')}:${String(m % 60).padStart(2,'0')}`);
  }
  return slots;
}

const DAY_NAMES      = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const DAY_FULL_NAMES = ['los domingos','los lunes','los martes','los miércoles','los jueves','los viernes','los sábados'];
const MONTH_NAMES    = ['enero','febrero','marzo','abril','mayo','junio',
                        'julio','agosto','septiembre','octubre','noviembre','diciembre'];

function formatDiasTrabajo(dias) {
  const nombres = [...dias].sort((a, b) => a - b).map(d => DAY_FULL_NAMES[d]);
  if (nombres.length === 1) return nombres[0];
  return nombres.slice(0, -1).join(', ') + ' y ' + nombres[nombres.length - 1];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DNI_RE   = /^\d{7,8}$/;

// Calcula cuándo enviar el recordatorio: 4 horas antes del turno,
// nunca antes de las 08:00 ni después de las 22:00 (hora Argentina, UTC-3).
function calcRemindAt(fechaStr, horaStr) {
  const [y, mo, d] = fechaStr.split('-').map(Number);
  const [h, mi]    = horaStr.split(':').map(Number);

  // Argentina = UTC-3  →  UTC = Argentina + 3
  const aptUtc    = new Date(Date.UTC(y, mo - 1, d, h + 3, mi));
  const remindUtc = new Date(aptUtc.getTime() - 4 * 3_600_000);

  // Hora Argentina del recordatorio
  const argH = (remindUtc.getUTCHours() - 3 + 24) % 24;

  if (argH >= 22) return null; // fuera de rango nocturno (no ocurre con slots 09-19)

  if (argH < 8) {
    // Adelantar al 08:00 Argentina (= 11:00 UTC) del mismo día Argentina
    const dayArg = new Date(remindUtc.getTime() - 3 * 3_600_000);
    return new Date(Date.UTC(
      dayArg.getUTCFullYear(), dayArg.getUTCMonth(), dayArg.getUTCDate(),
      11, 0, 0,  // 08:00 ARG = 11:00 UTC
    )).toISOString();
  }

  return remindUtc.toISOString();
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function isSameDay(a, b) {
  return a.getDate()===b.getDate() && a.getMonth()===b.getMonth() && a.getFullYear()===b.getFullYear();
}

// Devuelve true si el slot ya pasó (con 10 min de margen)
function isPastSlot(slot, date) {
  const now = new Date();
  if (!isSameDay(date, now)) return false;
  const [h, m] = slot.split(':').map(Number);
  const slotTime = new Date(now);
  slotTime.setHours(h, m + 10, 0, 0);
  return slotTime <= now;
}

export default function TenantPage() {
  const { slug } = useParams();

  const [tenant, setTenant]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);
  const [weekOffset, setWeekOffset]     = useState(0);
  const [takenSlots, setTakenSlots]     = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [cierresMap, setCierresMap]     = useState({});

  const [profesionales, setProfesionales]       = useState([]);
  const [selectedProf, setSelectedProf]         = useState(null);
  const [selectedSlot, setSelectedSlot]         = useState(null);
  const [form, setForm]                         = useState({ nombre: '', email: '', dni: '', telefono: '' });
  const [booking, setBooking]                   = useState(false);
  const [confirmed, setConfirmed]               = useState(null);
  const [bookingError, setBookingError]         = useState('');

  // Servicios y horarios del profesional seleccionado
  const [profServicios, setProfServicios]       = useState([]); // servicios del prof elegido
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [profHorarioMap, setProfHorarioMap]     = useState(null); // null = no cargado | {} = sin horario | {dia: {...}} = con horario

  useEffect(() => {
    async function load() {
      if (!/^[a-z0-9-]+$/i.test(slug)) { setLoading(false); setNotFound(true); return; }
      const { data } = await supabase
        .from('tenants').select('*').eq('slug', slug).single();
      setLoading(false);
      if (!data) { setNotFound(true); return; }
      setTenant(data);

      // Cargar profesionales activos del negocio
      const { data: profs } = await supabase
        .from('profesionales')
        .select('id, nombre')
        .eq('tenant_id', data.id)
        .eq('activo', true)
        .order('created_at');
      const lista = profs || [];
      setProfesionales(lista);
      // Si solo hay 1 profesional lo pre-seleccionamos (transparente para el cliente)
      if (lista.length === 1) setSelectedProf(lista[0]);
    }
    load();
  }, [slug]);

  // Carga los cierres para los 7 días visibles en el selector
  useEffect(() => {
    if (!tenant) return;
    const desde = new Date(today); desde.setDate(today.getDate() + weekOffset * 7);
    const hasta = new Date(desde);  hasta.setDate(desde.getDate() + 6);
    supabase
      .from('cierres')
      .select('fecha, hora_cierre')
      .eq('tenant_id', tenant.id)
      .gte('fecha', toDateStr(desde))
      .lte('fecha', toDateStr(hasta))
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(c => { m[c.fecha] = c; });
        setCierresMap(m);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, weekOffset]);

  useEffect(() => {
    if (!tenant) return;
    // Si hay múltiples profesionales y ninguno seleccionado, no cargamos slots aún
    if (profesionales.length > 1 && !selectedProf) {
      setTakenSlots([]);
      return;
    }
    async function loadSlots() {
      setLoadingSlots(true);
      setSelectedSlot(null);
      setConfirmed(null);
      const { data } = await supabase.rpc('get_available_slots', {
        p_tenant_id:      tenant.id,
        p_fecha:          toDateStr(selectedDate),
        p_profesional_id: selectedProf?.id ?? null,
      });
      setTakenSlots(data ? data.map(t => t.hora) : []);
      setLoadingSlots(false);
    }
    loadSlots();
  }, [tenant, selectedDate, selectedProf, profesionales.length]);

  // Cuando cambia el profesional seleccionado, carga sus servicios y horarios
  useEffect(() => {
    if (!selectedProf) {
      setProfServicios([]);
      setSelectedServicio(null);
      setProfHorarioMap(null);
      return;
    }
    async function loadProfData() {
      // Servicios
      const { data: psData } = await supabase
        .from('profesional_servicios')
        .select('servicios(id, nombre)')
        .eq('profesional_id', selectedProf.id);
      setProfServicios((psData || []).map(r => r.servicios).filter(Boolean));
      setSelectedServicio(null);

      // Horarios
      const { data: hData } = await supabase
        .from('horarios_profesional')
        .select('dia_semana, hora_inicio, hora_fin')
        .eq('profesional_id', selectedProf.id);
      if (!hData || hData.length === 0) {
        setProfHorarioMap({});
      } else {
        const map = {};
        hData.forEach(h => {
          map[h.dia_semana] = {
            hora_inicio: h.hora_inicio.substring(0, 5),
            hora_fin:    h.hora_fin.substring(0, 5),
          };
        });
        setProfHorarioMap(map);
      }
    }
    loadProfData();
  }, [selectedProf]);

  const handleBook = async () => {
    const nombre = form.nombre.trim();
    const email  = form.email.trim().toLowerCase();
    const dni    = form.dni.replace(/\D/g, ''); // solo dígitos
    const tel    = form.telefono.trim();

    if (nombre.length < 2)        { setBookingError('Ingresá tu nombre completo.'); return; }
    if (nombre.length > 80)       { setBookingError('El nombre es demasiado largo.'); return; }
    if (!EMAIL_RE.test(email))    { setBookingError('El email no es válido.'); return; }
    if (email.length > 120)       { setBookingError('El email es demasiado largo.'); return; }
    if (!DNI_RE.test(dni))        { setBookingError('El DNI no es válido (7 u 8 dígitos).'); return; }
    if (tel.length > 30)          { setBookingError('El teléfono no es válido.'); return; }
    if (isPastSlot(selectedSlot, selectedDate)) {
      setBookingError('Este horario ya pasó. Elegí otro.');
      setSelectedSlot(null);
      return;
    }

    setBooking(true); setBookingError('');
    const fechaStr    = toDateStr(selectedDate);
    const cancelToken = crypto.randomUUID();
    const remindAt    = calcRemindAt(fechaStr, selectedSlot);

    // Verificar límite mensual del plan (Plan Individual: 70 turnos/mes)
    if (tenant.plan === 'Individual') {
      const { data: count } = await supabase.rpc('get_monthly_turno_count', { p_tenant_id: tenant.id });
      if (count >= 70) {
        setBooking(false);
        setBookingError('Este negocio alcanzó el límite de turnos del mes. Intentá el mes que viene.');
        return;
      }
    }

    const { error } = await supabase.from('turnos').insert([{
      tenant_id:        tenant.id,
      fecha:            fechaStr,
      hora:             selectedSlot,
      cliente_nombre:   nombre,
      cliente_email:    email,
      cliente_dni:      dni,
      cliente_telefono: tel || null,
      cancel_token:     cancelToken,
      remind_at:        remindAt,
      profesional_id:   selectedProf?.id ?? null,
      servicio_id:      selectedServicio?.id ?? null,
    }]);

    if (error) {
      setBooking(false);
      if (error.code === '23505') setBookingError('Ese horario ya fue reservado. Elegí otro.');
      else setBookingError('Algo salió mal. Intentá de nuevo.');
      setTakenSlots(s => [...s, selectedSlot]);
      setSelectedSlot(null);
      return;
    }

    await supabase.functions.invoke('send-confirmation', {
      body: {
        cliente_nombre:     nombre,
        cliente_email:      email,
        hora:               selectedSlot,
        fecha:              fechaStr,
        negocio_nombre:     tenant.nombre,
        negocio_slug:       slug,
        cancel_token:       cancelToken,
        profesional_nombre: selectedProf?.nombre ?? null,
        servicio_nombre:    selectedServicio?.nombre ?? null,
      },
    });

    setBooking(false);
    setConfirmed({ hora: selectedSlot });
    setTakenSlots(s => [...s, selectedSlot]);
    setSelectedSlot(null);
    setForm({ nombre: '', email: '', dni: '', telefono: '' });
  };

  const slots = useMemo(() => generateSlots(tenant?.duracion_turno || 30), [tenant]);

  const MAX_WEEKS = 8; // hasta ~2 meses adelante
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + weekOffset * 7 + i);
    return d;
  });

  // Cierre del día seleccionado (null = abierto, { hora_cierre: null } = cerrado total,
  //  { hora_cierre: '15:00:00' } = cierre parcial)
  const cierreHoy = cierresMap[toDateStr(selectedDate)] ?? null;
  const diaFullCerrado = cierreHoy && cierreHoy.hora_cierre === null;

  const hasSchedule    = selectedProf && profHorarioMap && Object.keys(profHorarioMap).length > 0;
  const profNoTrabajaBoy = hasSchedule && !profHorarioMap[selectedDate.getDay()];
  const diasQueAtiende = hasSchedule ? Object.keys(profHorarioMap).map(Number) : [];

  // Filtra slots según cierre parcial: slot + duracion <= hora_cierre
  const toMin = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };
  const slotsEfectivos = useMemo(() => {
    const dur = tenant?.duracion_turno || 30;
    let s = slots;
    // Filtrar por cierre parcial
    if (cierreHoy && cierreHoy.hora_cierre !== null) {
      const closeMin = toMin(cierreHoy.hora_cierre.substring(0, 5));
      s = s.filter(slot => toMin(slot) + dur <= closeMin);
    }
    // Filtrar por horario del profesional (solo si tiene horarios configurados)
    if (selectedProf && profHorarioMap && Object.keys(profHorarioMap).length > 0) {
      const dia = selectedDate.getDay();
      const h   = profHorarioMap[dia];
      if (!h) {
        s = []; // no trabaja este día
      } else {
        const startMin = toMin(h.hora_inicio);
        const endMin   = toMin(h.hora_fin);
        s = s.filter(slot => toMin(slot) >= startMin && toMin(slot) + dur <= endMin);
      }
    }
    return s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, cierreHoy, tenant, selectedProf, profHorarioMap, selectedDate]);

  const availableCount = slotsEfectivos.filter(
    s => !takenSlots.includes(s) && !isPastSlot(s, selectedDate)
  ).length;

  if (loading) return (
    <div style={{ textAlign:'center', padding:'6rem', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
      Cargando...
    </div>
  );

  if (notFound) return (
    <div style={{ textAlign:'center', padding:'6rem 2rem', fontFamily:'var(--font-body)' }}>
      <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🔍</div>
      <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, marginBottom:'.5rem', color:'var(--text-primary)' }}>
        Página no encontrada
      </h2>
      <p style={{ color:'var(--text-secondary)', marginBottom:'2rem' }}>
        No existe ningún negocio en esta dirección.
      </p>
      <Link to="/" style={{ color:'var(--green-light)', fontWeight:600 }}>Volver al inicio</Link>
    </div>
  );

  if (!tenant.activo) return (
    <div style={{ textAlign:'center', padding:'6rem 2rem', fontFamily:'var(--font-body)' }}>
      <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>⏸️</div>
      <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, marginBottom:'.5rem', color:'var(--text-primary)' }}>
        Cuenta suspendida
      </h2>
      <p style={{ color:'var(--text-secondary)' }}>Este negocio no está disponible por el momento.</p>
    </div>
  );

  return (
    <div className="tenant-page" style={{ maxWidth:760, margin:'0 auto', padding:'24px 16px', fontFamily:'var(--font-body)' }}>

      {/* HEADER NEGOCIO */}
      <div style={{
        display:'flex', alignItems:'center', gap:16,
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-xl)', padding:'20px 22px', marginBottom:20,
        boxShadow:'var(--shadow)',
      }}>
        <div style={{
          width:52, height:52, borderRadius:'var(--r-lg)', flexShrink:0,
          background:'var(--green-bg)', border:'1px solid var(--green-border)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:26,
        }}>
          {tenant.emoji || '📅'}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{
            fontFamily:'var(--font-head)', fontWeight:800,
            fontSize:'clamp(1rem, 5vw, 1.25rem)',
            color:'var(--text-primary)', letterSpacing:'-0.02em',
            lineHeight:1.2,
            wordBreak:'break-word',
          }}>
            {tenant.nombre}
          </div>
          {tenant.descripcion && (
            <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:2 }}>
              {tenant.descripcion}
            </div>
          )}
        </div>
        <span style={{
          background:'var(--green-bg)', border:'1px solid var(--green-border)',
          color:'var(--green-light)', borderRadius:'var(--r-full)', padding:'4px 12px',
          fontSize:12, fontWeight:600, flexShrink:0,
        }}>
          Abierto
        </span>
      </div>

      {/* SELECTOR DE DÍA */}
      <div style={{
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-lg)', padding:'16px 18px', marginBottom:18,
        boxShadow:'var(--shadow)',
      }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
            ELEGÍ UN DÍA
          </div>
          <div style={{ fontSize:11, color:'var(--text-secondary)' }}>
            {MONTH_NAMES[days[0].getMonth()]}
            {days[0].getMonth() !== days[6].getMonth() ? ` / ${MONTH_NAMES[days[6].getMonth()]}` : ''}
            {' '}{days[0].getFullYear()}
          </div>
        </div>

        {/* Grid de días con flechas laterales */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {/* Flecha anterior */}
          <button
            onClick={() => {
              if (weekOffset === 0) return;
              const newOffset = weekOffset - 1;
              setWeekOffset(newOffset);
              const d = new Date(today);
              d.setDate(today.getDate() + newOffset * 7);
              setSelectedDate(d);
              setSelectedSlot(null);
            }}
            disabled={weekOffset === 0}
            style={{
              flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
              width:32, height:32, borderRadius:'var(--r-md)',
              border:'1px solid var(--border)',
              background: weekOffset === 0 ? 'transparent' : 'var(--bg3)',
              color: weekOffset === 0 ? 'var(--border)' : 'var(--text-primary)',
              cursor: weekOffset === 0 ? 'default' : 'pointer',
              fontSize:18, fontWeight:700, lineHeight:1,
              transition:'all .15s', touchAction:'manipulation',
            }}
          >{'<'}</button>

          {/* Días */}
          <div className="day-selector-grid" style={{ flex:1 }}>
            {days.map((d, i) => {
              const isSel     = isSameDay(d, selectedDate);
              const isToday   = isSameDay(d, today);
              const cierre    = cierresMap[toDateStr(d)];
              const cerrado   = cierre && cierre.hora_cierre === null;
              const noAtiende = hasSchedule && !profHorarioMap[d.getDay()];
              return (
                <button
                  key={i}
                  onClick={() => !cerrado && setSelectedDate(d)}
                  disabled={cerrado}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center', gap:3,
                    padding:'8px 2px', borderRadius:'var(--r-md)',
                    border: isSel ? '2px solid var(--green)' : cerrado ? '1px solid #fca5a5' : '1px solid var(--border)',
                    background: isSel ? 'var(--green-bg)' : cerrado ? '#fef2f2' : isToday ? 'var(--bg3)' : 'transparent',
                    cursor: cerrado ? 'default' : 'pointer', transition:'all .18s', touchAction:'manipulation',
                    opacity: cerrado ? 0.7 : noAtiende ? 0.5 : 1,
                  }}>
                  <span style={{ fontSize:10, color: cerrado ? '#ef4444' : 'var(--text-muted)', fontWeight:700, letterSpacing:'.04em' }}>
                    {DAY_NAMES[d.getDay()]}
                  </span>
                  <span style={{
                    fontFamily:'var(--font-head)', fontWeight:700, fontSize:'1rem',
                    color: isSel ? 'var(--green-light)' : cerrado ? '#dc2626' : 'var(--text-primary)',
                  }}>
                    {d.getDate()}
                  </span>
                  {isToday && !isSel && (
                    <span style={{ fontSize:8, color:'var(--green-light)', fontWeight:700 }}>hoy</span>
                  )}
                  {cerrado && (
                    <span style={{ fontSize:9, color:'#ef4444', fontWeight:700 }}>cerrado</span>
                  )}
                  {!cerrado && noAtiende && (
                    <span style={{ fontSize:9, color:'var(--text-muted)', fontWeight:700 }}>no atiende</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Flecha siguiente */}
          <button
            onClick={() => {
              if (weekOffset >= MAX_WEEKS - 1) return;
              const newOffset = weekOffset + 1;
              setWeekOffset(newOffset);
              const d = new Date(today);
              d.setDate(today.getDate() + newOffset * 7);
              setSelectedDate(d);
              setSelectedSlot(null);
            }}
            disabled={weekOffset >= MAX_WEEKS - 1}
            style={{
              flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
              width:32, height:32, borderRadius:'var(--r-md)',
              border:'1px solid var(--border)',
              background: weekOffset >= MAX_WEEKS - 1 ? 'transparent' : 'var(--green-bg)',
              color: weekOffset >= MAX_WEEKS - 1 ? 'var(--border)' : 'var(--green-light)',
              borderColor: weekOffset >= MAX_WEEKS - 1 ? 'var(--border)' : 'var(--green-border)',
              cursor: weekOffset >= MAX_WEEKS - 1 ? 'default' : 'pointer',
              fontSize:18, fontWeight:700, lineHeight:1,
              transition:'all .15s', touchAction:'manipulation',
            }}
          >{'>'}</button>
        </div>
      </div>

      {/* CONFIRMACIÓN */}
      {confirmed && (
        <div style={{
          background:'var(--green-bg)', border:'1px solid var(--green-border)',
          borderRadius:'var(--r-lg)', padding:'14px 20px', marginBottom:18,
          display:'flex', alignItems:'center', gap:12,
        }}>
          <span style={{ fontSize:20, flexShrink:0 }}>✅</span>
          <div>
            <div style={{ fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-head)', fontSize:15 }}>
              ¡Turno confirmado! — {confirmed.hora}
            </div>
            <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:2 }}>
              Te enviamos un email con los detalles y un link para cancelar si necesitás.
            </div>
          </div>
        </div>
      )}

      {/* SELECTOR DE PROFESIONAL (solo si hay 2 o más) */}
      {profesionales.length > 1 && (
        <div style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:'var(--r-lg)', padding:'16px 18px', marginBottom:18,
          boxShadow:'var(--shadow)',
        }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:10 }}>
            ELEGÍ UN PROFESIONAL
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {profesionales.map(p => {
              const isSel = selectedProf?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => { setSelectedProf(isSel ? null : p); setSelectedSlot(null); }}
                  style={{
                    padding:'8px 18px', borderRadius:'var(--r-full)',
                    border: isSel ? '2px solid var(--green)' : '1px solid var(--border)',
                    background: isSel ? 'var(--green-bg)' : 'transparent',
                    color: isSel ? 'var(--green-light)' : 'var(--text-primary)',
                    fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                    cursor:'pointer', transition:'all .15s',
                  }}
                >
                  {p.nombre}
                </button>
              );
            })}
          </div>
          {!selectedProf && (
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:8 }}>
              Seleccioná un profesional para ver los horarios disponibles.
            </div>
          )}
        </div>
      )}

      {/* SELECTOR DE SERVICIO (solo si el profesional tiene servicios configurados) */}
      {selectedProf && profServicios.length > 0 && (
        <div style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:'var(--r-lg)', padding:'16px 18px', marginBottom:18,
          boxShadow:'var(--shadow)',
        }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:10 }}>
            ELEGÍ UN SERVICIO
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {profServicios.map(s => {
              const isSel = selectedServicio?.id === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => { setSelectedServicio(isSel ? null : s); setSelectedSlot(null); }}
                  style={{
                    padding:'8px 18px', borderRadius:'var(--r-full)',
                    border: isSel ? '2px solid var(--green)' : '1px solid var(--border)',
                    background: isSel ? 'var(--green-bg)' : 'transparent',
                    color: isSel ? 'var(--green-light)' : 'var(--text-primary)',
                    fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                    cursor:'pointer', transition:'all .15s',
                  }}
                >
                  {s.nombre}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* GRILLA DE TURNOS */}
      <div style={{
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-xl)', padding:'20px', boxShadow:'var(--shadow)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
            {DAY_NAMES[selectedDate.getDay()].toUpperCase()} {selectedDate.getDate()} DE {MONTH_NAMES[selectedDate.getMonth()].toUpperCase()}
            {selectedProf && <span style={{ fontWeight:400, color:'var(--green-light)', marginLeft:8 }}>· {selectedProf.nombre}</span>}
          </div>
          {!loadingSlots && !diaFullCerrado && (profesionales.length <= 1 || selectedProf) && (
            <span style={{ fontSize:12, color:'var(--text-secondary)' }}>
              {availableCount} disponibles
            </span>
          )}
        </div>

        {profesionales.length > 1 && !selectedProf ? (
          <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>
            Seleccioná un profesional arriba para ver los horarios.
          </div>
        ) : loadingSlots ? (
          <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>
            Cargando horarios...
          </div>
        ) : diaFullCerrado ? (
          <div style={{ textAlign:'center', padding:'2rem 1rem' }}>
            <div style={{ fontSize:'2rem', marginBottom:10 }}>🔒</div>
            <div style={{ fontWeight:700, color:'var(--text-primary)', marginBottom:6 }}>Local cerrado este día</div>
            <div style={{ fontSize:13, color:'var(--text-muted)' }}>Elegí otro día para reservar tu turno.</div>
          </div>
        ) : profNoTrabajaBoy ? (
          <div style={{ textAlign:'center', padding:'2rem 1rem' }}>
            <div style={{ fontSize:'2rem', marginBottom:10 }}>📅</div>
            <div style={{ fontWeight:700, color:'var(--text-primary)', marginBottom:6 }}>
              {selectedProf.nombre} no atiende este día
            </div>
            <div style={{ fontSize:13, color:'var(--text-muted)' }}>
              Solo atiende {formatDiasTrabajo(diasQueAtiende)}.
            </div>
          </div>
        ) : (
          <div className="slots-grid">
            {slotsEfectivos.map(slot => {
              const taken = takenSlots.includes(slot);
              const past  = isPastSlot(slot, selectedDate);
              const isSel = selectedSlot === slot;
              const disabled = taken || past;
              return (
                <button
                  key={slot}
                  disabled={disabled}
                  onClick={() => { setSelectedSlot(isSel ? null : slot); setBookingError(''); }}
                  style={{
                    padding:'11px 0', borderRadius:'var(--r-md)', textAlign:'center',
                    border: isSel ? '2px solid var(--green)' : '1px solid var(--border)',
                    background: isSel ? 'var(--green-bg)' : 'transparent',
                    color: isSel ? 'var(--green-light)' : disabled ? 'var(--text-muted)' : 'var(--text-primary)',
                    fontFamily:'var(--font-body)', fontSize:14, fontWeight: isSel ? 700 : 500,
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.35 : 1,
                    textDecoration: taken ? 'line-through' : 'none',
                    transition:'all .15s', touchAction:'manipulation',
                  }}
                >
                  {slot}
                </button>
              );
            })}
          </div>
        )}

        {/* FORM DE RESERVA */}
        {selectedSlot && (
          <div style={{ marginTop:18, paddingTop:18, borderTop:'1px solid var(--border)' }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:14 }}>
              TUS DATOS — TURNO {selectedSlot}
            </div>
            <div className="booking-form-grid">
              {[
                { key:'nombre', label:'NOMBRE', type:'text',  placeholder:'Tu nombre' },
                { key:'email',  label:'EMAIL',  type:'email', placeholder:'tu@email.com' },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:5 }}>
                    {label}
                  </label>
                  <input
                    type={type}
                    placeholder={placeholder}
                    value={form[key]}
                    maxLength={key === 'nombre' ? 80 : 120}
                    autoComplete={key === 'nombre' ? 'name' : 'email'}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="booking-input"
                  />
                </div>
              ))}
            </div>
            <div style={{ marginBottom:14, marginTop:12 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:5 }}>
                DNI
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="36123456"
                value={form.dni}
                maxLength={10}
                autoComplete="off"
                onChange={e => setForm(f => ({ ...f, dni: e.target.value }))}
                className="booking-input"
              />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', display:'block', marginBottom:5 }}>
                TELÉFONO <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(opcional)</span>
              </label>
              <input
                type="tel"
                placeholder="+54 9 11 1234-5678"
                value={form.telefono}
                maxLength={30}
                autoComplete="tel"
                onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
                className="booking-input"
              />
            </div>
            {bookingError && (
              <p style={{ color:'#f87171', fontSize:13, marginBottom:10 }}>{bookingError}</p>
            )}
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={handleBook}
                disabled={booking}
                style={{
                  flex:1, padding:'13px', borderRadius:'var(--r-full)',
                  background:'var(--green)', color:'#fff', border:'none',
                  fontFamily:'var(--font-body)', fontSize:15, fontWeight:600,
                  cursor: booking ? 'default' : 'pointer', opacity: booking ? .7 : 1,
                  transition:'all .2s', touchAction:'manipulation',
                }}
              >
                {booking ? 'Confirmando...' : `Confirmar ${selectedSlot}`}
              </button>
              <button
                onClick={() => { setSelectedSlot(null); setBookingError(''); }}
                style={{
                  padding:'13px 18px', borderRadius:'var(--r-full)',
                  background:'transparent', border:'1px solid var(--border)',
                  color:'var(--text-secondary)', fontFamily:'var(--font-body)',
                  fontSize:13, cursor:'pointer', touchAction:'manipulation',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ textAlign:'center', marginTop:28 }}>
        <span style={{ fontSize:12, color:'var(--text-muted)' }}>
          Agenda gestionada con{' '}
          <Link to="/" style={{ color:'var(--green-light)', fontWeight:600, textDecoration:'none' }}>
            turnokey
          </Link>
        </span>
      </div>
    </div>
  );
}
